import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  useColorModeValue,
  Container
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyEmailVerificationCode, logout } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    const handleVerification = async () => {
      const oobCode = searchParams.get('oobCode');
      const mode = searchParams.get('mode');

      // Check if this is an email verification action
      if (mode !== 'verifyEmail' || !oobCode) {
        setStatus('error');
        setErrorMessage('Invalid verification link. Please check your email for the correct link.');
        return;
      }

      try {
        // Apply the verification code
        await applyEmailVerificationCode(oobCode);
        
        // Log out user if they're logged in (they need to sign in again after verification)
        if (user) {
          await logout();
        }

        setStatus('success');
        
        // Redirect to login page after 2 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Email verified successfully! You can now sign in.',
              messageType: 'success'
            } 
          });
        }, 2000);
      } catch (error: any) {
        console.error('Email verification error:', error);
        setStatus('error');
        
        let message = 'Failed to verify email. The link may have expired or already been used.';
        if (error.message?.includes('expired')) {
          message = 'This verification link has expired. Please request a new verification email.';
        } else if (error.message?.includes('already')) {
          message = 'This email has already been verified. You can sign in now.';
        } else if (error.message) {
          message = error.message;
        }
        
        setErrorMessage(message);
      }
    };

    handleVerification();
  }, [searchParams, navigate, user]);

  return (
    <Container maxW="500px" py={12}>
      <Box
        bg={bgColor}
        borderRadius="xl"
        p={8}
        boxShadow="lg"
        border="1px solid"
        borderColor={useColorModeValue('gray.200', 'gray.600')}
      >
        <VStack gap={6} align="stretch" textAlign="center">
          {status === 'verifying' && (
            <>
              <Center>
                <Spinner size="xl" color="purple.500" thickness="4px" />
              </Center>
              <VStack gap={2}>
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  Verifying Your Email
                </Text>
                <Text color={labelColor}>
                  Please wait while we verify your email address...
                </Text>
              </VStack>
            </>
          )}

          {status === 'success' && (
            <>
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  <VStack gap={2} align="start">
                    <Text fontWeight="bold">Email Verified Successfully!</Text>
                    <Text fontSize="sm">
                      Your email has been verified. Redirecting you to the login page...
                    </Text>
                  </VStack>
                </AlertDescription>
              </Alert>
              <Button
                colorScheme="purple"
                onClick={() => navigate('/login', { 
                  state: { 
                    message: 'Email verified successfully! You can now sign in.',
                    messageType: 'success'
                  } 
                })}
              >
                Go to Login
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  <VStack gap={2} align="start">
                    <Text fontWeight="bold">Verification Failed</Text>
                    <Text fontSize="sm">{errorMessage}</Text>
                  </VStack>
                </AlertDescription>
              </Alert>
              <VStack gap={3}>
                <Button
                  colorScheme="purple"
                  onClick={() => navigate('/login')}
                  w="100%"
                >
                  Go to Login
                </Button>
                <Button
                  variant="outline"
                  colorScheme="purple"
                  onClick={() => navigate('/signup')}
                  w="100%"
                >
                  Sign Up Again
                </Button>
              </VStack>
            </>
          )}
        </VStack>
      </Box>
    </Container>
  );
};

export default EmailVerification;

